import { useAuth } from "@/contexts/AuthContext";
import { useTerritory } from "@/contexts/TerritoryContext";
import { GRADE_GROUPS, TERRITORIES, examLabelForGrade, examStageForGrade, type TerritoryCode } from "@shared/territories";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Check, Eye, ChevronDown, RotateCcw } from "lucide-react";

/**
 * Admin preview switcher.
 *
 * This is a testing tool, not a student control, so it says plainly what it is doing: which grade
 * is being previewed, which exam that grade is actually working towards, and which territory's
 * offering is on screen.
 *
 * The old version listed grades 4 to 11 as a flat list of numbers. That hid two things that
 * matter. It gave no clue that Grade 6 and Grade 11 are different products rather than different
 * years, and it stopped at 11, so CAPE could not be previewed at all. Grouping by exam makes the
 * shape of the catalogue visible at a glance, which is the point when you are checking the whole
 * Caribbean offering rather than one dashboard.
 */
export function AdminPreviewSwitcher() {
  const { user, adminGradeOverride, setAdminGradeOverride } = useAuth();
  const { territoryCode, setTerritory, territory } = useTerritory();

  if (!user || user.role !== "admin") return null;

  const grade = Number(user.grade);
  const stage = examStageForGrade(grade);
  const examLabel = examLabelForGrade(grade, territory.examName);
  const previewing = adminGradeOverride != null;

  const gradeLabel =
    GRADE_GROUPS.flatMap(g => g.grades).find(g => g.grade === grade)?.label ?? `Grade ${grade || "?"}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-1.5 text-xs font-medium ${previewing ? "border-primary bg-primary/10 text-primary" : ""}`}
          data-testid="button-admin-preview"
        >
          {previewing && <Eye className="h-3.5 w-3.5" />}
          <span className="tabular-nums">{gradeLabel}</span>
          {examLabel && (
            <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary">
              {examLabel}
            </span>
          )}
          <span className="text-muted-foreground">{territory.flag}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-xs">
          Preview as a student
          <p className="mt-0.5 text-[11px] font-normal text-muted-foreground">
            Changes what you see. Nothing about your account changes.
          </p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {GRADE_GROUPS.map(group => {
          // The primary exam is territory-specific; CSEC and CAPE are regional and read the same
          // in every market, which is why only this row takes its name from the territory.
          const heading = group.stage === "PRIMARY" ? `${territory.examName} (primary)` : group.label;
          return (
            <div key={group.stage}>
              <DropdownMenuLabel className="pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {heading}
              </DropdownMenuLabel>
              {group.grades.map(g => (
                <DropdownMenuItem
                  key={g.grade}
                  onSelect={() => setAdminGradeOverride(g.grade)}
                  className="gap-2 text-sm"
                  data-testid={`preview-grade-${g.grade}`}
                >
                  <Check className={`h-4 w-4 ${grade === g.grade ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1">{g.label}</span>
                  {group.stage === "LOWER_SECONDARY" && (
                    <span className="text-[10px] text-muted-foreground">no exam</span>
                  )}
                </DropdownMenuItem>
              ))}
            </div>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Territory
        </DropdownMenuLabel>
        {(Object.keys(TERRITORIES) as TerritoryCode[]).map(code => {
          const t = TERRITORIES[code];
          return (
            <DropdownMenuItem
              key={code}
              onSelect={() => setTerritory(code)}
              className="gap-2 text-sm"
              data-testid={`preview-territory-${code}`}
            >
              <Check className={`h-4 w-4 ${territoryCode === code ? "opacity-100" : "opacity-0"}`} />
              <span className="flex-1">{t.flag} {t.name}</span>
              <span className="text-[10px] font-semibold text-muted-foreground">{t.examName}</span>
            </DropdownMenuItem>
          );
        })}

        {previewing && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setAdminGradeOverride(null)}
              className="gap-2 text-sm font-medium text-primary"
              data-testid="preview-reset"
            >
              <RotateCcw className="h-4 w-4" />
              Back to my own grade
            </DropdownMenuItem>
          </>
        )}

        {stage === "CAPE" && (
          <p className="px-2 py-2 text-[11px] leading-snug text-muted-foreground">
            CAPE has its syllabus structure seeded but no questions yet, so most tiles will be empty.
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
